<!--
   Created by Terence on 2024/6/23 - 15:12
-->
<template>
  <div class="lt" v-if="hasData">
    <div class="lt-info">
      <LtInfo />
    </div>
    <div class="lt-publisher">
      <Publisher @onRefresh="handleRefresh" />
    </div>
    <div class="lt-summary">
      <LtSummary @onRefresh="handleRefresh" />
    </div>
    <div class="lt-content">
      <LtContent />
    </div>
    <div class="lt-actions">
      <LtActions ref="ltActionsRef" />
    </div>
  </div>
  <LinjieEmpty
    extClass="emptypage"
    v-else-if="ltInfoCode === ERROR_TYPES.DELETED"
    text="页面已被删除啦"
  />
  <LinjieEmpty
    extClass="emptypage"
    v-else-if="ltInfoCode === ERROR_TYPES.ILLEGAL"
    text="页面因违规不可见，此抽选已终止，不可再继续参与或开奖"
  />
  <div v-else-if="showDefaultError" class="load-error">
    <LinjieEmpty text="抽选加载失败" />
    <button type="button" @click="handleRefresh">重新加载</button>
  </div>
</template>

<script setup>
import {
  ref,
  onMounted,
  onBeforeUnmount,
  computed,
  provide,
  getCurrentInstance,
  nextTick,
  watch,
} from "vue";
import { storeToRefs } from "pinia";
import { useUsersStore } from "@/store/user";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import jb from "@frontend/effuse";
import LtInfo from "./components/LtInfo.vue";
import LtSummary from "./components/LtSummary.vue";
import LtContent from "./components/LtContent.vue";
import LtActions from "./components/LtActions.vue";
import Publisher from "@/components/Publisher/index.vue";
import LinjieEmpty from "@/components/LinjieEmpty/index.vue";
import LotteryApi from "@/apis/lottery";
import { getQuery, updateUrlWithWhitelist } from "@/utils/url";
import { REQUEST_OK } from "@/constant/network";
import { isLinjieApp } from "@/utils/env-related";
import { showToast } from "vant";

const inst = getCurrentInstance();

const userStore = useUsersStore();
const inApp = isLinjieApp();

const { userInfo } = storeToRefs(userStore);

const ERROR_TYPES = {
  DELETED: 5900001,
  ILLEGAL: 5900002,
};

const ltInfo = ref("");
const ltInfoCode = ref(0);
const inited = ref(false);
const ltActionsRef = ref("ltActionsRef");
let requestSeq = 0;
let mounted = false;
let disposed = false;
let initialized = false;

const isPublisherRef = computed(() => {
  return ltInfo.value?.publisher?.id === userInfo.value?.userId;
});

const hasData = computed(() => {
  return (
    (!inApp || userStore.appUserReady) &&
    !!ltInfo.value?.id &&
    ltInfoCode.value === REQUEST_OK
  );
});

const showDefaultError = computed(() => {
  return (
    inited.value &&
    !hasData.value &&
    ltInfoCode.value !== ERROR_TYPES.DELETED &&
    ltInfoCode.value !== ERROR_TYPES.ILLEGAL
  );
});

const ltInfoRef = computed(() => {
  return ltInfo.value;
});

const publisherRef = computed(() => {
  return ltInfo.value.publisher;
});

const invalidateDetail = () => {
  requestSeq++;
  ltInfo.value = "";
  ltInfoCode.value = 0;
  inited.value = false;
};

const initEvent = async () => {
  invalidateDetail();
  if (disposed || (inApp && !userStore.appUserReady)) return 0;

  const currentRequest = requestSeq;
  const syncId = userStore.appUserSyncId;
  const isCurrent = () =>
    !disposed &&
    currentRequest === requestSeq &&
    (!inApp || (userStore.appUserReady && userStore.appUserSyncId === syncId));

  try {
    const { id } = getQuery();
    const res = await LotteryApi.getLotteryInfo({ id });
    if (!isCurrent()) return 0;

    inited.value = true;
    ltInfoCode.value = res?.code ?? -1;
    if (res?.code !== REQUEST_OK || !res.data?.id) return 0;
    ltInfo.value = res.data;
    return currentRequest;
  } catch (error) {
    if (!isCurrent()) return 0;
    inited.value = true;
    ltInfoCode.value = -1;
    throw error;
  }
};

const handleRefresh = async () => {
  try {
    const completedRequest = await initEvent();
    if (!completedRequest) return;
    await nextTick();
    if (completedRequest !== requestSeq || !hasData.value) return;

    inst?.refs?.ltActionsRef?.initActionConfig();
    setShareInfo();
    if (!initialized) {
      initialized = true;
      processQuery();
      handleInitEvent();
    }
  } catch {
    // 当前请求失败后保留错误态，用户可以重新加载。
  }
};

const processQuery = () => {
  const { showWinnerPopup } = getQuery();

  if (showWinnerPopup === "YES") {
    inst?.refs?.ltActionsRef?.handleForceShowWinners();
  }
};

const setShareInfo = () => {
  const shareInfo = {
    title: ltInfo.value.title,
    content: ltInfo.value.description,
    url: updateUrlWithWhitelist(["id"]),
    imageUrl: ltInfo.value.cover + "!mfit_w720_h720_webp",
  };

  // window.isInFlutter以后不应该再用, 这是为了临界&千岛合并 做的狗屎兼容
  if (window.isInFlutter) {
    // 老的 临界app
    jb.shareUrl(shareInfo);
  } else {
    jb.onShareAppMessage({
      title: shareInfo.title,
      imageUrl: shareInfo.imageUrl,
      path: shareInfo.url,
    });
  }
};

watch(
  () => [userStore.appUserSyncId, userStore.appUserReady],
  () => {
    if (!inApp) return;
    invalidateDetail();
    if (mounted && userStore.appUserReady) handleRefresh();
  },
  { flush: "sync" }
);

onMounted(() => {
  mounted = true;
  if (!inApp || userStore.appUserReady) handleRefresh();
});

onBeforeUnmount(() => {
  disposed = true;
  invalidateDetail();
});

function handleInitEvent() {
  // 2025 临界春节活动
  if (localStorage.getItem("SHARE_PROVE_EDITED") === "SHARE_PROVE_EDITED") {
    localStorage.removeItem("SHARE_PROVE_EDITED");
    showToast("提交成功，请等待开奖哦");
  }
}

provide("ltInfo", ltInfoRef);
provide("publisher", publisherRef);
provide("isPublisher", isPublisherRef);
provide("handleRefreshPage", handleRefresh);
</script>

<style lang="scss" scoped>
.lt {
  background-color: #f7f7f9;
  &-info {
    background-color: #fff;
  }
  &-publisher {
    margin-top: 16rpx;
    padding: 24rpx 30rpx;
    background-color: #fff;
  }
  &-summary {
    margin-top: 16rpx;
    padding: 24rpx 30rpx;
    background-color: #fff;
  }
  &-content {
    margin-top: 16rpx;
    padding: 24rpx 30rpx;
    background-color: #fff;
    padding-bottom: 200rpx;
  }
  &-actions {
    position: fixed;
    //z-index: 100000;
    bottom: 0;
    left: 0;
    width: 100%;
    padding-bottom: 68rpx;
    background-color: #fff;
  }
}

.emptypage {
  padding: 0 50rpx;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.load-error {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24rpx;

  button {
    padding: 16rpx 32rpx;
    border: 1px solid #ddd;
    border-radius: 8rpx;
    background: #fff;
    color: #333;
  }
}
</style>
